import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { firestore, storage } from "./firebase_config";
import { Constants } from "../constant";
import type {
  CreateHappeningNowInput,
  HappeningNowDoc,
  HappeningNowListItem,
  UpdateHappeningNowInput,
} from "../types/happeningNow";
import {
  ActivityDomains,
  appendActivity,
  type ActivityActor,
  type ActivityAppendInput,
} from "./activityService";
import { normalizeHappeningNowPlatforms } from "./happeningNowPlatforms";

const ID_PATTERN = /^[a-z0-9]{1,32}$/;

async function logActivitySafe(entry: ActivityAppendInput): Promise<void> {
  try {
    await appendActivity(entry);
  } catch (e) {
    console.error("Failed to record activity:", e);
  }
}

function isValidHttpUrl(raw: string): boolean {
  const s = raw.trim();
  if (!s || s.length > 2048) return false;
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

function randomId(length = 12): string {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < length; i++) {
    out += chars[bytes[i]! % chars.length];
  }
  return out;
}

async function idExists(id: string): Promise<boolean> {
  // Check both draft and live so a future republish can't collide with a card
  // that's still pinned in some snapshot (we mint fresh ids on republish).
  const [draftSnap, liveSnap] = await Promise.all([
    getDoc(doc(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION, id)),
    getDoc(doc(firestore, Constants.HAPPENING_NOW_COLLECTION, id)),
  ]);
  return draftSnap.exists() || liveSnap.exists();
}

async function allocateUniqueId(): Promise<string> {
  for (let attempt = 0; attempt < 12; attempt++) {
    const candidate = randomId(12);
    if (!ID_PATTERN.test(candidate)) continue;
    if (!(await idExists(candidate))) return candidate;
  }
  throw new Error("Could not allocate a unique id. Try again.");
}

function parseExpiresAt(local: string | undefined): Timestamp {
  if (!local || !local.trim()) {
    throw new Error("Expiry date is required.");
  }
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) {
    throw new Error("Invalid expiry date.");
  }
  return Timestamp.fromDate(d);
}

/**
 * Parse the optional scheduled-start field. Empty / undefined means the card is
 * visible immediately, so we return `null`. When set, it must fall before
 * `expiresAt` and no more than ~1 month from now.
 */
function parseStartsAt(
  local: string | undefined,
  expiresAt: Timestamp,
): Timestamp | null {
  if (!local || !local.trim()) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) {
    throw new Error("Invalid start date.");
  }
  const startsAt = Timestamp.fromDate(d);
  if (startsAt.toMillis() >= expiresAt.toMillis()) {
    throw new Error("Start date must be before the expiry date.");
  }
  const maxStartMs =
    Date.now() +
    Constants.HAPPENING_NOW_MAX_START_AHEAD_DAYS * 24 * 60 * 60 * 1000;
  if (startsAt.toMillis() > maxStartMs) {
    throw new Error("Start date can be at most 1 month from now.");
  }
  return startsAt;
}

/** `datetime-local` value from a Firestore timestamp (local timezone). */
export function timestampToDatetimeLocalValue(ts: Timestamp): string {
  const d = ts.toDate();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function sanitizeFilename(name: string): string {
  const dot = name.lastIndexOf(".");
  const stem =
    (dot >= 0 ? name.slice(0, dot) : name)
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 40) || "image";
  const ext =
    (dot >= 0 ? name.slice(dot + 1) : "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")
      .slice(0, 5) || "bin";
  return `${Date.now()}-${stem}.${ext}`;
}

function readImageDimensions(
  file: File,
): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !window.URL?.createObjectURL) {
      resolve(null);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

function validateImageFile(file: File): void {
  if (!file.type.startsWith("image/")) {
    throw new Error("Only image files (PNG / JPG / WebP) are accepted.");
  }
  if (file.size > Constants.HAPPENING_NOW_IMAGE_MAX_BYTES) {
    throw new Error("Image must be 2 MB or smaller.");
  }
}

async function uploadImage(
  itemId: string,
  file: File,
): Promise<{
  url: string;
  path: string;
  width: number | null;
  height: number | null;
}> {
  validateImageFile(file);
  const dims = await readImageDimensions(file);
  const path = `${Constants.HAPPENING_NOW_STORAGE_PREFIX}/${itemId}/${sanitizeFilename(
    file.name,
  )}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file, { contentType: file.type });
  const url = await getDownloadURL(ref);
  return {
    url,
    path,
    width: dims?.width ?? null,
    height: dims?.height ?? null,
  };
}

async function nextOrder(): Promise<number> {
  const snap = await getDocs(
    query(collection(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION)),
  );
  let max = 0;
  snap.forEach((d) => {
    const v = (d.data() as HappeningNowDoc).order;
    if (typeof v === "number" && v > max) max = v;
  });
  return max + 10;
}

async function nextLiveOrder(): Promise<number> {
  const snap = await getDocs(
    query(collection(firestore, Constants.HAPPENING_NOW_COLLECTION)),
  );
  let max = 0;
  snap.forEach((d) => {
    const v = (d.data() as HappeningNowDoc).order;
    if (typeof v === "number" && v > max) max = v;
  });
  return max + 10;
}

export async function createHappeningNowItem(
  input: CreateHappeningNowInput,
  uid: string,
  actor?: ActivityActor | null,
): Promise<{ id: string }> {
  const title = input.title.trim();
  if (!title) throw new Error("Title is required.");
  if (title.length > 120)
    throw new Error("Title must be 120 characters or fewer.");
  const description = input.description?.trim() ?? "";
  if (description.length > 500)
    throw new Error("Description must be 500 characters or fewer.");
  const targetLink = input.targetLink.trim();
  if (!isValidHttpUrl(targetLink))
    throw new Error("Target link must be a valid http(s) URL.");

  const expiresAt = parseExpiresAt(input.expiresAtLocal);
  const startsAt = parseStartsAt(input.startsAtLocal, expiresAt);
  if (!input.platforms?.length) {
    throw new Error("Pick at least one platform (Android and/or iOS).");
  }
  const platforms = normalizeHappeningNowPlatforms(input.platforms);
  const id = await allocateUniqueId();
  const upload = await uploadImage(id, input.imageFile);
  const order = await nextOrder();
  const creatorDisplayName = input.creatorDisplayName.trim() || uid;

  const payload: Record<string, unknown> = {
    id,
    title,
    imageUrl: upload.url,
    imagePath: upload.path,
    targetLink,
    expiresAt,
    active: input.active,
    order,
    platforms,
    createdBy: uid,
    createdByDisplayName: creatorDisplayName,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (startsAt) payload.startsAt = startsAt;
  if (description) payload.description = description;
  if (upload.width) payload.imageWidth = upload.width;
  if (upload.height) payload.imageHeight = upload.height;

  await setDoc(
    doc(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION, id),
    payload,
  );

  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "create_draft",
      resourceType: "happening_now_item",
      resourceId: id,
      summary: `Drafted Happening now card "${title}"`,
      actor,
      details: { targetLink, active: input.active },
    });
  }

  return { id };
}

/**
 * Create a card directly on the **live** carousel (skips draft). Mobile sees it
 * immediately. Does not create a publication record — use "Publish carousel"
 * from draft for versioned history.
 */
export async function createHappeningNowLiveItem(
  input: CreateHappeningNowInput,
  uid: string,
  actor?: ActivityActor | null,
): Promise<{ id: string }> {
  const title = input.title.trim();
  if (!title) throw new Error("Title is required.");
  if (title.length > 120)
    throw new Error("Title must be 120 characters or fewer.");
  const description = input.description?.trim() ?? "";
  if (description.length > 500)
    throw new Error("Description must be 500 characters or fewer.");
  const targetLink = input.targetLink.trim();
  if (!isValidHttpUrl(targetLink))
    throw new Error("Target link must be a valid http(s) URL.");

  const expiresAt = parseExpiresAt(input.expiresAtLocal);
  const startsAt = parseStartsAt(input.startsAtLocal, expiresAt);
  if (!input.platforms?.length) {
    throw new Error("Pick at least one platform (Android and/or iOS).");
  }
  const platforms = normalizeHappeningNowPlatforms(input.platforms);
  const id = await allocateUniqueId();
  const upload = await uploadImage(id, input.imageFile);
  const order = await nextLiveOrder();
  const creatorDisplayName = input.creatorDisplayName.trim() || uid;

  const payload: Record<string, unknown> = {
    id,
    title,
    imageUrl: upload.url,
    imagePath: upload.path,
    targetLink,
    expiresAt,
    active: input.active,
    order,
    platforms,
    clickCount: 0,
    lastClickedAt: null,
    createdBy: uid,
    createdByDisplayName: creatorDisplayName,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (startsAt) payload.startsAt = startsAt;
  if (description) payload.description = description;
  if (upload.width) payload.imageWidth = upload.width;
  if (upload.height) payload.imageHeight = upload.height;

  await setDoc(doc(firestore, Constants.HAPPENING_NOW_COLLECTION, id), payload);

  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "create_live",
      resourceType: "happening_now_item",
      resourceId: id,
      summary: `Published Happening now card live "${title}" (skipped draft)`,
      actor,
      details: { targetLink, active: input.active },
    });
  }

  return { id };
}

export async function updateHappeningNowItem(
  id: string,
  uid: string,
  input: UpdateHappeningNowInput,
  actor?: ActivityActor | null,
): Promise<void> {
  const ref = doc(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new Error("Item not found.");
  }
  const existing = snap.data() as HappeningNowDoc;

  const title = input.title.trim();
  if (!title) throw new Error("Title is required.");
  if (title.length > 120)
    throw new Error("Title must be 120 characters or fewer.");
  const description = input.description?.trim() ?? "";
  if (description.length > 500)
    throw new Error("Description must be 500 characters or fewer.");
  const targetLink = input.targetLink.trim();
  if (!isValidHttpUrl(targetLink))
    throw new Error("Target link must be a valid http(s) URL.");
  const expiresAt = parseExpiresAt(input.expiresAtLocal);
  const startsAt = parseStartsAt(input.startsAtLocal, expiresAt);
  if (!input.platforms?.length) {
    throw new Error("Pick at least one platform (Android and/or iOS).");
  }
  const platforms = normalizeHappeningNowPlatforms(input.platforms);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const update: { [k: string]: any } = {
    title,
    targetLink,
    expiresAt,
    // Cleared start reverts to "visible immediately".
    startsAt: startsAt ?? deleteField(),
    active: input.active,
    platforms,
    description: description ? description : deleteField(),
    // Operator restored an item — clear the deletion flag so the next janitor
    // run won't re-flag it (assumes operator picked a new expiresAt too).
    markedForDeletion: deleteField(),
    markedForDeletionAt: deleteField(),
    updatedAt: serverTimestamp(),
  };

  if (input.imageFile) {
    const upload = await uploadImage(id, input.imageFile);
    update.imageUrl = upload.url;
    update.imagePath = upload.path;
    update.imageWidth = upload.width ?? deleteField();
    update.imageHeight = upload.height ?? deleteField();
    // Best-effort cleanup of old image — don't fail the update if it's already gone.
    if (existing.imagePath && existing.imagePath !== upload.path) {
      deleteObject(storageRef(storage, existing.imagePath)).catch((e) => {
        console.warn("Could not delete old image:", e);
      });
    }
  }

  await updateDoc(ref, update);

  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "update_draft",
      resourceType: "happening_now_item",
      resourceId: id,
      summary: `Updated draft card "${title}"`,
      actor,
      details: {
        targetLink,
        active: input.active,
        replacedImage: Boolean(input.imageFile),
      },
    });
  }
  void uid;
}

/**
 * Update a **live** carousel card in place (CMS). Preserves `createdBy`,
 * `createdAt`, and click telemetry — only mutates content fields + `updatedAt`.
 */
export async function updateLiveHappeningNowItem(
  id: string,
  uid: string,
  input: UpdateHappeningNowInput,
  actor?: ActivityActor | null,
): Promise<void> {
  const ref = doc(firestore, Constants.HAPPENING_NOW_COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new Error("Live card not found.");
  }
  const existing = snap.data() as HappeningNowDoc;

  const title = input.title.trim();
  if (!title) throw new Error("Title is required.");
  if (title.length > 120)
    throw new Error("Title must be 120 characters or fewer.");
  const description = input.description?.trim() ?? "";
  if (description.length > 500)
    throw new Error("Description must be 500 characters or fewer.");
  const targetLink = input.targetLink.trim();
  if (!isValidHttpUrl(targetLink))
    throw new Error("Target link must be a valid http(s) URL.");
  const expiresAt = parseExpiresAt(input.expiresAtLocal);
  const startsAt = parseStartsAt(input.startsAtLocal, expiresAt);
  if (!input.platforms?.length) {
    throw new Error("Pick at least one platform (Android and/or iOS).");
  }
  const platforms = normalizeHappeningNowPlatforms(input.platforms);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const update: { [k: string]: any } = {
    title,
    targetLink,
    expiresAt,
    // Cleared start reverts to "visible immediately".
    startsAt: startsAt ?? deleteField(),
    active: input.active,
    platforms,
    description: description ? description : deleteField(),
    markedForDeletion: deleteField(),
    markedForDeletionAt: deleteField(),
    updatedAt: serverTimestamp(),
  };

  if (input.imageFile) {
    const upload = await uploadImage(id, input.imageFile);
    update.imageUrl = upload.url;
    update.imagePath = upload.path;
    update.imageWidth = upload.width ?? deleteField();
    update.imageHeight = upload.height ?? deleteField();
    if (existing.imagePath && existing.imagePath !== upload.path) {
      deleteObject(storageRef(storage, existing.imagePath)).catch((e) => {
        console.warn("Could not delete old image:", e);
      });
    }
  }

  await updateDoc(ref, update);

  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "update_live",
      resourceType: "happening_now_item",
      resourceId: id,
      summary: `Updated live Happening now card "${title}"`,
      actor,
      details: {
        targetLink,
        active: input.active,
        replacedImage: Boolean(input.imageFile),
      },
    });
  }
  void uid;
}

/**
 * Remove a card from the **live** carousel. Does not delete the draft (if any).
 * Storage image is left for the janitor / publication references, same as draft delete.
 */
export async function deleteLiveHappeningNowItem(
  id: string,
  actor?: ActivityActor | null,
): Promise<void> {
  const ref = doc(firestore, Constants.HAPPENING_NOW_COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Live card not found.");
  const data = snap.data() as HappeningNowDoc;
  await deleteDoc(ref);
  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "delete_live",
      resourceType: "happening_now_item",
      resourceId: id,
      summary: `Removed live card "${data.title}"`,
      actor,
    });
  }
}

export async function setHappeningNowActive(
  id: string,
  active: boolean,
  actor?: ActivityActor | null,
): Promise<void> {
  const ref = doc(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Item not found.");
  await updateDoc(ref, { active, updatedAt: serverTimestamp() });
  if (actor) {
    const data = snap.data() as HappeningNowDoc;
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "toggle_draft",
      resourceType: "happening_now_item",
      resourceId: id,
      summary: `${active ? "Enabled" : "Paused"} draft "${data.title}"`,
      actor,
      details: { active },
    });
  }
}

/**
 * Delete a card from the working draft. The matching live card (if any) stays
 * untouched until the next publish — that's intentional so an accidental draft
 * delete doesn't yank the card out of the live carousel.
 *
 * The image is preserved if any historical publication still references it; the
 * janitor's `imagePathsUsed` index keeps storage GC honest.
 */
export async function deleteHappeningNowItem(
  id: string,
  actor?: ActivityActor | null,
): Promise<void> {
  const ref = doc(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Item not found.");
  const data = snap.data() as HappeningNowDoc;

  // Don't delete the storage image yet — it may still be referenced by the live
  // doc or a publication snapshot. The janitor handles orphaned image cleanup.
  await deleteDoc(ref);

  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "delete_draft",
      resourceType: "happening_now_item",
      resourceId: id,
      summary: `Removed draft card "${data.title}"`,
      actor,
    });
  }
}

/**
 * Persist a new visual ordering of the **draft**. `orderedIds` is the list as
 * the operator left it (top → bottom). We reassign `order` to evenly-spaced
 * integers so future single-card reorders only touch one doc.
 */
export async function reorderHappeningNowItems(
  orderedIds: string[],
  actor?: ActivityActor | null,
): Promise<void> {
  if (orderedIds.length === 0) return;
  const batch = writeBatch(firestore);
  orderedIds.forEach((id, index) => {
    const ref = doc(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION, id);
    batch.update(ref, {
      order: (index + 1) * 10,
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();

  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "reorder_draft",
      resourceType: "happening_now_item",
      resourceId: "carousel",
      summary: `Reordered draft carousel (${orderedIds.length} cards)`,
      actor,
      details: { orderedIds },
    });
  }
}

function sortByOrderThenCreatedDesc(
  a: HappeningNowListItem,
  b: HappeningNowListItem,
): number {
  const ao = typeof a.order === "number" ? a.order : Number.MAX_SAFE_INTEGER;
  const bo = typeof b.order === "number" ? b.order : Number.MAX_SAFE_INTEGER;
  if (ao !== bo) return ao - bo;
  return b.createdAt.toMillis() - a.createdAt.toMillis();
}

/** List the operator's working draft (CMS view). */
export async function listHappeningNowItems(): Promise<HappeningNowListItem[]> {
  const snap = await getDocs(
    query(collection(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION)),
  );
  const rows: HappeningNowListItem[] = [];
  snap.forEach((d) => {
    rows.push(d.data() as HappeningNowListItem);
  });
  rows.sort(sortByOrderThenCreatedDesc);
  return rows;
}

/**
 * List the *live* (currently published) carousel — what the mobile app reads.
 * The CMS shows this in its own section so the operator can always see what
 * users are seeing, plus reorder / pause without going through publish.
 */
export async function listLiveHappeningNowItems(): Promise<
  HappeningNowListItem[]
> {
  const snap = await getDocs(
    query(collection(firestore, Constants.HAPPENING_NOW_COLLECTION)),
  );
  const rows: HappeningNowListItem[] = [];
  snap.forEach((d) => {
    rows.push(d.data() as HappeningNowListItem);
  });
  rows.sort(sortByOrderThenCreatedDesc);
  return rows;
}

// ---------------------------------------------------------------------------
// Live-side mutations (no publish ceremony)
//
// Reorder and pause/resume are quick fixes the operator wants to make without
// going through draft → publish. We mirror these changes into the draft for
// any matching ids so a subsequent publish doesn't undo the operator's intent.
// ---------------------------------------------------------------------------

/**
 * Persist a new visual ordering of the LIVE carousel. `orderedIds` is the
 * list as the operator left it (top → bottom). Matching draft cards get the
 * same `order` so they stay in sync when the next publish happens.
 */
export async function reorderLiveHappeningNowItems(
  orderedIds: string[],
  actor?: ActivityActor | null,
): Promise<void> {
  if (orderedIds.length === 0) return;
  // Look up which ids also exist in the draft so we only mirror order changes
  // for matching cards (don't accidentally `update` a non-existent draft doc).
  const draftSnap = await getDocs(
    query(collection(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION)),
  );
  const draftIds = new Set<string>();
  draftSnap.forEach((d) => draftIds.add(d.id));

  const batch = writeBatch(firestore);
  orderedIds.forEach((id, index) => {
    const order = (index + 1) * 10;
    batch.update(doc(firestore, Constants.HAPPENING_NOW_COLLECTION, id), {
      order,
      updatedAt: serverTimestamp(),
    });
    if (draftIds.has(id)) {
      batch.update(
        doc(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION, id),
        { order, updatedAt: serverTimestamp() },
      );
    }
  });
  await batch.commit();

  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "reorder_live",
      resourceType: "happening_now_item",
      resourceId: "carousel",
      summary: `Reordered live carousel (${orderedIds.length} cards)`,
      actor,
      details: { orderedIds },
    });
  }
}

/**
 * Pause or resume a single LIVE card. Mirrors to the matching draft card if
 * one exists, so the next publish doesn't flip it back.
 */
export async function setLiveHappeningNowActive(
  id: string,
  active: boolean,
  actor?: ActivityActor | null,
): Promise<void> {
  const liveRef = doc(firestore, Constants.HAPPENING_NOW_COLLECTION, id);
  const liveSnap = await getDoc(liveRef);
  if (!liveSnap.exists()) throw new Error("Live card not found.");

  const draftRef = doc(firestore, Constants.HAPPENING_NOW_DRAFT_COLLECTION, id);
  const draftSnap = await getDoc(draftRef);

  const batch = writeBatch(firestore);
  batch.update(liveRef, { active, updatedAt: serverTimestamp() });
  if (draftSnap.exists()) {
    batch.update(draftRef, { active, updatedAt: serverTimestamp() });
  }
  await batch.commit();

  if (actor) {
    const data = liveSnap.data() as HappeningNowDoc;
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "toggle_live",
      resourceType: "happening_now_item",
      resourceId: id,
      summary: `${active ? "Resumed" : "Paused"} live "${data.title}"`,
      actor,
      details: { active, mirroredToDraft: draftSnap.exists() },
    });
  }
}

// ---------------------------------------------------------------------------
// Live carousel settings (parent doc of the live items subcollection)
//
// Carousel-wide config lives on the `happeningNow/live` document — mobile reads
// it alongside the `items` subcollection. Today that's just `parallaxEnabled`:
// a 3D scroll effect toggle. Replaces the old `happening_now_parallax` RTDB
// feature flag (kept in place until mobile migrates to reading this field).
// ---------------------------------------------------------------------------

/** Read the carousel-wide parallax toggle. Missing doc / field ⇒ disabled. */
export async function getLiveCarouselParallaxEnabled(): Promise<boolean> {
  const snap = await getDoc(doc(firestore, Constants.HAPPENING_NOW_LIVE_DOC));
  if (!snap.exists()) return false;
  const data = snap.data() as { parallaxEnabled?: boolean };
  return data.parallaxEnabled === true;
}

/** Toggle the carousel-wide parallax effect for the live carousel. */
export async function setLiveCarouselParallaxEnabled(
  enabled: boolean,
  actor?: ActivityActor | null,
): Promise<void> {
  // merge so we never clobber other settings that may live on this doc later.
  await setDoc(
    doc(firestore, Constants.HAPPENING_NOW_LIVE_DOC),
    { parallaxEnabled: enabled, parallaxUpdatedAt: serverTimestamp() },
    { merge: true },
  );
  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.HappeningNow,
      action: "toggle_parallax",
      resourceType: "happening_now_settings",
      resourceId: "live",
      summary: `${enabled ? "Enabled" : "Disabled"} parallax mode on the live carousel`,
      actor,
      details: { parallaxEnabled: enabled },
    });
  }
}
